import { useState } from "react";
import { toast } from "sonner";
import { applyMentee, applyMentor, applyVolunteer } from "../lib/api";
import { MENTOR_INTERESTS, VOLUNTEER_CATEGORIES } from "../lib/content";
import { Loader2 } from "lucide-react";

function PageHeader({ overline, title, subtitle }) {
  return (
    <section className="bg-[#FDFBF7] border-b border-black/10">
      <div className="container-x py-20 md:py-24">
        <span className="overline text-[#6B21A8]">{overline}</span>
        <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-6 max-w-2xl text-black/70 text-lg">{subtitle}</p>}
      </div>
    </section>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-black/80 mb-2">{label}{required && <span className="text-[#6B21A8]"> *</span>}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full rounded-md border border-black/15 bg-white px-4 py-3 outline-none focus:border-[#6B21A8]";

export function BecomeAMentee() {
  const [f, setF] = useState({
    student_first_name: "", student_last_name: "", student_age: "", student_grade: "", school: "",
    student_interests: "", student_goals: "",
    parent_name: "", parent_email: "", parent_phone: "",
    emergency_contact: "", referral_source: "", additional_notes: "", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Parent consent is required.");
    setLoading(true);
    try {
      await applyMentee({ ...f, student_age: Number(f.student_age) });
      toast.success("Application submitted — we'll be in touch within 3 business days.");
      setF({ student_first_name: "", student_last_name: "", student_age: "", student_grade: "", school: "", student_interests: "", student_goals: "", parent_name: "", parent_email: "", parent_phone: "", emergency_contact: "", referral_source: "", additional_notes: "", consent: false });
    } catch { toast.error("Submission failed. Please try again."); } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader
        overline="Become a Mentee"
        title="Step into brotherhood."
        subtitle="Enrollment is free. Parents/guardians submit the form below — we'll reach out for a family meeting within 3 business days."
      />
      <section className="bg-white">
        <div className="container-x py-16 max-w-3xl">
          <form onSubmit={submit} className="space-y-6" data-testid="mentee-form">
            <fieldset className="space-y-4">
              <legend className="overline text-[#6B21A8] mb-2">Student information</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name" required><input required className={inputCls} value={f.student_first_name} onChange={set("student_first_name")} data-testid="mentee-first" /></Field>
                <Field label="Last name" required><input required className={inputCls} value={f.student_last_name} onChange={set("student_last_name")} data-testid="mentee-last" /></Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Age" required><input required type="number" min="10" max="17" className={inputCls} value={f.student_age} onChange={set("student_age")} data-testid="mentee-age" /></Field>
                <Field label="Grade" required><input required className={inputCls} value={f.student_grade} onChange={set("student_grade")} data-testid="mentee-grade" /></Field>
                <Field label="School" required><input required className={inputCls} value={f.school} onChange={set("school")} data-testid="mentee-school" /></Field>
              </div>
              <Field label="Interests"><textarea rows="3" className={inputCls} value={f.student_interests} onChange={set("student_interests")} data-testid="mentee-interests" placeholder="Sports, music, coding, art…" /></Field>
              <Field label="Goals"><textarea rows="3" className={inputCls} value={f.student_goals} onChange={set("student_goals")} data-testid="mentee-goals" placeholder="What do you want to get out of the program?" /></Field>
            </fieldset>

            <fieldset className="space-y-4 pt-6 border-t border-black/10">
              <legend className="overline text-[#6B21A8] mb-2">Parent / Guardian</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" required><input required className={inputCls} value={f.parent_name} onChange={set("parent_name")} data-testid="mentee-parent-name" /></Field>
                <Field label="Phone" required><input required className={inputCls} value={f.parent_phone} onChange={set("parent_phone")} data-testid="mentee-parent-phone" /></Field>
              </div>
              <Field label="Email" required><input required type="email" className={inputCls} value={f.parent_email} onChange={set("parent_email")} data-testid="mentee-parent-email" /></Field>
              <Field label="Emergency contact"><input className={inputCls} value={f.emergency_contact} onChange={set("emergency_contact")} data-testid="mentee-emergency" placeholder="Name & phone" /></Field>
              <Field label="How did you hear about us?"><input className={inputCls} value={f.referral_source} onChange={set("referral_source")} data-testid="mentee-referral" /></Field>
              <Field label="Anything else we should know?"><textarea rows="3" className={inputCls} value={f.additional_notes} onChange={set("additional_notes")} data-testid="mentee-notes" /></Field>
            </fieldset>

            <label className="flex items-start gap-3 pt-2">
              <input type="checkbox" checked={f.consent} onChange={set("consent")} data-testid="mentee-consent" className="mt-1 h-4 w-4 rounded border-black/30 text-[#6B21A8] focus:ring-[#6B21A8]" />
              <span className="text-sm text-black/70">I am the parent/legal guardian and I consent to my child's participation, photos taken during programming, and to be contacted by the organization.</span>
            </label>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="mentee-submit">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : "Submit application"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

export function BecomeAMentor() {
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", occupation: "", experience: "",
    interests: [], availability: "", references: "", background_check_consent: false, signature: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleInterest = (v) => setF((s) => ({ ...s, interests: s.interests.includes(v) ? s.interests.filter((x) => x !== v) : [...s.interests, v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.background_check_consent) return toast.error("Background check consent is required.");
    if (!f.signature.trim()) return toast.error("Please add your digital signature.");
    setLoading(true);
    try {
      await applyMentor(f);
      toast.success("Application received — a program director will reach out within 5 business days.");
      setF({ first_name: "", last_name: "", email: "", phone: "", occupation: "", experience: "", interests: [], availability: "", references: "", background_check_consent: false, signature: "" });
    } catch { toast.error("Submission failed. Please try again."); } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader
        overline="Become a Mentor"
        title="Show up. Change a life."
        subtitle="We look for consistent adults who can commit to 4 hours a month for at least one program cycle."
      />
      <section className="bg-white">
        <div className="container-x py-16 max-w-3xl">
          <form onSubmit={submit} className="space-y-6" data-testid="mentor-form">
            <fieldset className="space-y-4">
              <legend className="overline text-[#6B21A8] mb-2">Personal information</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name" required><input required className={inputCls} value={f.first_name} onChange={set("first_name")} data-testid="mentor-first" /></Field>
                <Field label="Last name" required><input required className={inputCls} value={f.last_name} onChange={set("last_name")} data-testid="mentor-last" /></Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" required><input required type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="mentor-email" /></Field>
                <Field label="Phone" required><input required className={inputCls} value={f.phone} onChange={set("phone")} data-testid="mentor-phone" /></Field>
              </div>
              <Field label="Occupation"><input className={inputCls} value={f.occupation} onChange={set("occupation")} data-testid="mentor-occupation" /></Field>
              <Field label="Relevant experience"><textarea rows="3" className={inputCls} value={f.experience} onChange={set("experience")} data-testid="mentor-experience" placeholder="Youth work, coaching, teaching, mentoring…" /></Field>
            </fieldset>

            <fieldset className="space-y-3 pt-6 border-t border-black/10">
              <legend className="overline text-[#6B21A8] mb-2">Areas of interest</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="mentor-interests">
                {MENTOR_INTERESTS.map((i) => (
                  <label key={i} className={`flex items-center gap-3 rounded-md border px-4 py-2.5 cursor-pointer text-sm ${f.interests.includes(i) ? "border-[#6B21A8] bg-[#6B21A8]/5" : "border-black/15"}`}>
                    <input type="checkbox" checked={f.interests.includes(i)} onChange={() => toggleInterest(i)} className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" data-testid={`mentor-interest-${i.replace(/\s+/g, "-").toLowerCase()}`} />
                    {i}
                  </label>
                ))}
              </div>
              <Field label="Availability"><input className={inputCls} value={f.availability} onChange={set("availability")} data-testid="mentor-availability" placeholder="e.g. Saturdays, weekday evenings" /></Field>
              <Field label="References"><textarea rows="3" className={inputCls} value={f.references} onChange={set("references")} data-testid="mentor-references" placeholder="Name, relationship, phone/email — 2 references preferred" /></Field>
            </fieldset>

            <div className="pt-6 border-t border-black/10 space-y-4">
              <label className="flex items-start gap-3">
                <input type="checkbox" checked={f.background_check_consent} onChange={set("background_check_consent")} data-testid="mentor-consent" className="mt-1 h-4 w-4 rounded border-black/30 text-[#6B21A8]" />
                <span className="text-sm text-black/70">I consent to a background check as required for mentoring young men.</span>
              </label>
              <Field label="Digital signature (type your full name)" required>
                <input required className={inputCls} value={f.signature} onChange={set("signature")} data-testid="mentor-signature" />
              </Field>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="mentor-submit">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : "Submit application"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

export function Volunteer() {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", categories: [], availability: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleCat = (v) => setF((s) => ({ ...s, categories: s.categories.includes(v) ? s.categories.filter((x) => x !== v) : [...s.categories, v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.categories.length === 0) return toast.error("Pick at least one way you'd like to help.");
    setLoading(true);
    try {
      await applyVolunteer(f);
      toast.success("Thanks! We'll be in touch about your first shift.");
      setF({ first_name: "", last_name: "", email: "", phone: "", categories: [], availability: "", notes: "" });
    } catch { toast.error("Submission failed."); } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader
        overline="Volunteer"
        title="Give an hour. Change a Saturday."
        subtitle="From tutoring to cooking to photography — every hour matters. Pick what fits your gifts."
      />
      <section className="bg-white">
        <div className="container-x py-16 max-w-3xl">
          <form onSubmit={submit} className="space-y-6" data-testid="volunteer-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" required><input required className={inputCls} value={f.first_name} onChange={set("first_name")} data-testid="vol-first" /></Field>
              <Field label="Last name" required><input required className={inputCls} value={f.last_name} onChange={set("last_name")} data-testid="vol-last" /></Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" required><input required type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="vol-email" /></Field>
              <Field label="Phone" required><input required className={inputCls} value={f.phone} onChange={set("phone")} data-testid="vol-phone" /></Field>
            </div>
            <div>
              <p className="text-sm font-medium text-black/80 mb-3">Where would you like to help? <span className="text-[#6B21A8]">*</span></p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="vol-categories">
                {VOLUNTEER_CATEGORIES.map((c) => (
                  <label key={c} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer text-sm ${f.categories.includes(c) ? "border-[#6B21A8] bg-[#6B21A8]/5" : "border-black/15"}`}>
                    <input type="checkbox" checked={f.categories.includes(c)} onChange={() => toggleCat(c)} className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" data-testid={`vol-cat-${c.toLowerCase()}`} />
                    {c}
                  </label>
                ))}
              </div>
            </div>
            <Field label="Availability"><input className={inputCls} value={f.availability} onChange={set("availability")} data-testid="vol-availability" placeholder="Days & times you're generally free" /></Field>
            <Field label="Anything else?"><textarea rows="3" className={inputCls} value={f.notes} onChange={set("notes")} data-testid="vol-notes" /></Field>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="vol-submit">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : "Submit"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
